import { CSSProperties, useCallback, useMemo } from 'react';

import { PDP_LIST_ID, PDP_LIST_NAME } from '~/features/tracking/constants';
import { useProductClickTracking } from '~/features/tracking/hooks/useProductClickTracking';
import { IVariation } from '~/lib/data-contract';

import styles from './productPickerTrack.module.css';

import { ProductPickerImage } from '../ProductPickerImage/ProductPickerImage';
import { ProductPickerItem } from '../ProductPickerItem/ProductPickerItem';
import { ProductPickerLabel } from '../ProductPickerLabel/ProductPickerLabel';
import { ProductPickerPane } from '../ProductPickerPane/ProductPickerPane';

const trackSize = 4;

const isIndexVisible = (total: number, index: number) => {
	if (total <= trackSize) {
		return true;
	}
	return index < trackSize - 1;
};

export type ProductPickerTrackType = 'size' | 'color';

type ProductPickerTrackRenderProps = {
	type: ProductPickerTrackType;
	variant: IVariation;
	onClick: (variation: IVariation) => void;
	active?: string;
	transitioningToUrl?: string;
};

const ProductPickerTrackRender = ({ type, variant, onClick, active, transitioningToUrl }: ProductPickerTrackRenderProps) => {
	const trackProductClick = useProductClickTracking(variant, {
		listName: PDP_LIST_NAME,
		listId: PDP_LIST_ID,
	});

	const isActive = type === 'size' ? active === variant.sizeAttribute : active === variant.color?.colorId;

	const transitioningTo = transitioningToUrl === variant.url;
	const transitioningToElsewhere = !!transitioningToUrl && !transitioningTo;

	const handleClick = useCallback(() => {
		trackProductClick();
		onClick(variant);
	}, [variant, trackProductClick, onClick]);

	return (
		<ProductPickerItem
			transitioningTo={transitioningTo}
			transitioningToElsewhere={transitioningToElsewhere}
			onClick={handleClick}
			active={isActive}
			collapsed
		>
			{type === 'color' ? <ProductPickerImage image={variant.image} /> : <ProductPickerLabel>{variant.sizeAttribute}</ProductPickerLabel>}
		</ProductPickerItem>
	);
};

type ProductPickerTrackProps = {
	variants: IVariation[];
	type: ProductPickerTrackType; // Defines how each item in the track are displayed.
	onVariationClick: (variation: IVariation) => void;
	active?: string;
	title: string;
	transitioningToUrl?: string;
};

export const ProductPickerTrack = ({ variants, type, onVariationClick, active, title, transitioningToUrl }: ProductPickerTrackProps) => {
	const reSortedVariants = useMemo(() => {
		const currentSelectionIndex = type === 'size' ? variants.findIndex((variant) => variant.sizeAttribute === active) : variants.findIndex((variant) => variant.color?.colorId === active);
		const isCurrentVisible = isIndexVisible(variants.length, currentSelectionIndex);
		if (!isCurrentVisible) {
			const sorted = [...variants];
			const selection = sorted.splice(currentSelectionIndex, 1);
			sorted.splice(trackSize - 2, 0, ...selection);
			return sorted;
		}
		return variants;
	}, [variants, type, active]);

	return (
		<ul
			className={styles.root}
			style={{ '--picker-track-size': trackSize } as CSSProperties}
		>
			{reSortedVariants.slice(0, trackSize - 1).map((variant) => (
				<ProductPickerTrackRender
					key={variant.sku}
					onClick={onVariationClick}
					type={type}
					variant={variant}
					active={active}
					transitioningToUrl={transitioningToUrl}
				/>
			))}
			{variants.length < trackSize
				? Array(trackSize - variants.length)
						.fill(null)
						.map((_, i) => (
							<ProductPickerItem
								key={i}
								collapsed
							/>
						))
				: null}
			{variants.length === trackSize ? (
				<ProductPickerTrackRender
					onClick={onVariationClick}
					type={type}
					variant={reSortedVariants[variants.length - 1]}
					active={active}
					transitioningToUrl={transitioningToUrl}
				/>
			) : null}
			{variants.length > trackSize ? (
				<ProductPickerPane
					variants={variants}
					quantity={variants.length}
					title={title}
					type={type}
					onVariationClick={onVariationClick}
				/>
			) : null}
		</ul>
	);
};
